import { Injectable } from '@angular/core';
import {
  AuthSession,
  confirmSignUp,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  signUp,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { ApplicationConfig } from '../../core/config/application.config';
import { AuthToken } from '../../domain/auth/model/auth-token';
import {
  AuthProvider,
  LoginResult,
} from '../../domain/auth/model/auth-provider';
import { CognitoAuthErrorUtil } from '../util/cognito-auth-error.util';

@Injectable({ providedIn: 'root' })
export class CognitoAuthService implements AuthProvider {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: ApplicationConfig.cognito!.userPoolId,
          userPoolClientId: ApplicationConfig.cognito!.userPoolWebClientId,
          loginWith: {
            email: true,
          },
        },
      },
    });
  }

  public async login(username: string, password: string): Promise<LoginResult> {
    try {
      return signIn({ username, password }).then(result => {
        let nextStep = 'DONE';

        switch (result.nextStep.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            nextStep = 'SET_PASSWORD';
            break;
        }

        return { nextStep } as LoginResult;
      });
    } catch (ex: any) {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(ex, 'LOGIN');
      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      throw error;
    }
  }

  public async setPassword(password: string, username?: string): Promise<any> {
    try {
      return await confirmSignIn({
        challengeResponse: password,
        options: {
          userAttributes: {
            given_name: '',
            family_name: '',
          },
        },
      });
    } catch (ex: any) {
      const message = String(ex?.message ?? ex ?? '');
      const shouldFallback =
        message.includes('given_name') ||
        message.includes('family_name') ||
        message.includes('Invalid attributes');

      if (!shouldFallback || !username) {
        const mapped = CognitoAuthErrorUtil.getErrorMessage(ex, 'SET_PASSWORD');
        const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
        (error as any).cause = ex;
        throw error;
      }

      try {
        return await confirmSignIn({
          challengeResponse: password,
          options: {
            userAttributes: {
              given_name: username,
              family_name: username,
            },
          },
        });
      } catch (fallbackEx: any) {
        const mapped = CognitoAuthErrorUtil.getErrorMessage(
          fallbackEx,
          'SET_PASSWORD'
        );
        const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
        (error as any).cause = fallbackEx;
        throw error;
      }
    }
  }

  public async changePassword(
    currentPassword: string,
    password: string
  ): Promise<any> {
    try {
      return updatePassword({
        oldPassword: currentPassword,
        newPassword: password,
      });
    } catch (ex: any) {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'CHANGE_PASSWORD'
      );
      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      throw error;
    }
  }

  public async confirmResetPassword(
    username: string,
    confirmationCode: string,
    password: string
  ): Promise<any> {
    try {
      return confirmResetPassword({
        username,
        confirmationCode,
        newPassword: password,
      });
    } catch (ex: any) {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'RESET_PASSWORD_CONFIRM'
      );
      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      throw error;
    }
  }

  public resetPassword(username: string): Promise<any> {
    return resetPassword({ username }).catch(ex => {
      const mapped = CognitoAuthErrorUtil.getErrorMessage(
        ex,
        'RESET_PASSWORD_START'
      );

      if (mapped.silent) {
        return Promise.resolve();
      }

      const error = new Error(mapped.key ?? 'ERROR.GENERAL_ERROR');
      (error as any).cause = ex;
      return Promise.reject(error);
    });
  }

  public async signUp(username: string, password: string): Promise<void> {
    await signUp({
      username,
      password,
      options: {
        userAttributes: {
          email: username,
        },
      },
    });
  }

  public async confirmSignUp(username: string, code: string): Promise<void> {
    await confirmSignUp({ username, confirmationCode: code });
  }

  public async resendSignUpCode(username: string): Promise<void> {
    await resendSignUpCode({ username });
  }

  public async logout() {
    try {
      await signOut({ global: true });
    } catch {
      await signOut();
    }
  }

  public async refresh(): Promise<AuthToken> {
    return fetchAuthSession({ forceRefresh: true }).then(session =>
      this.generateToken(session)
    );
  }

  public async generateToken(session?: AuthSession): Promise<AuthToken> {
    const { tokens } = session || (await fetchAuthSession());
    const { idToken } = tokens!;
    if (!idToken) {
      throw new Error('Missing idToken');
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    const exp = idToken.payload['exp'];
    const expiresIn =
      typeof exp === 'number' ? Math.max(exp - nowSeconds, 0) : 0;

    return {
      accessToken: idToken.toString(),
      refreshToken: '',
      expiresIn,
      email: idToken.payload['email'] ?? '',
      userId: 0,
      tokenType: 'Bearer',
      scope: '',
      changePasswordRequired: false,
    } as AuthToken;
  }
}
